/**
 * Builds a published price sheet from a catalog seed.
 *
 * Units at each stage — the three representations meet here:
 *   in     integer µ$ cost rows (CatalogSeed) and basis points
 *   middle integer cents, from deriveHwPricing's single HALF_EVEN rounding
 *   out    DOLLARS, via centsToDollars — what productDb stores, what the price
 *          book serves, and what a human reads in a published edition
 *
 * The sheet is keyed by productSku then by quarter LABEL ("2026-Q3"), not by
 * productDb's pricingQxYYYY key. priceBookFromEdition does that translation, so
 * the published artefact stays readable and independent of productDb's shape —
 * which matters because productDb is on its way out.
 */
import { CatalogSeed, snapshotFromSeed } from './seedBuilder';
import { deriveHwPricing } from './engine';
import { centsToDollars } from './money';

export interface SkuSheetEntry {
  /**
   * The EARLIEST priced quarter's discount, as a fraction (0.3). This was the
   * only discount a sheet could carry before per-quarter support. Retained
   * because an edition published before that change has it and nothing else,
   * and because it is what productDb's own scalar `discount` maps to. Prefer
   * `discounts` for anything quarter-scoped.
   */
  discount: number;
  prices: Record<string, number>;      // quarter label -> dollars (NET)
  /**
   * Quarter label -> discount fraction. A discount resolves per quarter from
   * `effectiveFrom` rows (a later-dated CMP-DISC-HW or CMP-DISC-<sku> row is
   * the supported way to change one), so a sheet spanning 14 quarters can
   * legitimately carry more than one.
   *
   * OPTIONAL, and `sheetFormat` deliberately does not bump for it: editions are
   * immutable, a revert to an older one must still price correctly, and a
   * format bump would make `priceBookFromEdition` throw for every client still
   * running older JS. Absent means "one discount for the whole sheet, and it is
   * `discount`".
   */
  discounts?: Record<string, number>;
}

export type PriceSheet = Record<string, SkuSheetEntry>;

export function buildSheet(
  seed: CatalogSeed, quarters: string[], asOf?: string,
): PriceSheet {
  const out: PriceSheet = {};
  // Pins SkuSheetEntry.discount — the pre-existing scalar — to whichever quarter
  // first prices each SKU (quarters iterate ascending, sorted below). The real
  // per-quarter values go in `discounts`; this exists so an older reader sees
  // the same value it always did. Held in basis points (integers) because these
  // get compared, and float equality is a trap.
  const firstDiscountBp: Record<string, { quarter: string; bp: number }> = {};
  for (const quarter of [...quarters].sort()) {
    // snapshotFromSeed, not snapshotFromRows: an empty resolution before the
    // first authored quarter is legitimate here (the SKU is simply absent from
    // the sheet for that quarter), and must not throw.
    const priced = deriveHwPricing(snapshotFromSeed(seed, quarter, asOf));
    for (const [sku, price] of Object.entries(priced)) {
      firstDiscountBp[sku] ??= { quarter, bp: price.discountBp };
      const entry = out[sku]
        ?? { discount: firstDiscountBp[sku]!.bp / 10000, prices: {}, discounts: {} };
      entry.prices[quarter] = centsToDollars(price.discountCents);
      (entry.discounts ??= {})[quarter] = price.discountBp / 10000;
      out[sku] = entry;
    }
  }
  return out;
}
